// PWA install + service worker registration. Captures the beforeinstallprompt
// event so we can defer the browser's native A2HS banner and trigger it on our
// own UX cue (typically a button click on second visit). Also detects iOS,
// where beforeinstallprompt isn't fired and we instead show an instructions
// modal. The service worker is registered alongside so PWA install criteria
// are met (HTTPS + manifest + SW). Safe to import in the browser only.

let deferredPrompt = null;
const listeners = new Set();
let installed = false;
let lastEvent = null;

function notify() {
  const state = { canInstall: !!deferredPrompt, installed, isIOS: detectIOS(), isStandalone: detectStandalone() };
  for (const cb of listeners) { try { cb(state); } catch (_) {} }
}

function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  // iPad on iOS 13+ reports as MacIntel; check touch points to disambiguate.
  const ua = navigator.userAgent || '';
  const isIPhoneIPad = /iPad|iPhone|iPod/.test(ua);
  const isMacTouch = /Mac/.test(ua) && navigator.maxTouchPoints > 1;
  return isIPhoneIPad || isMacTouch;
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari uses navigator.standalone
  return window.navigator?.standalone === true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    lastEvent = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

// Trigger the native install prompt. Returns 'accepted' | 'dismissed' | null.
export async function triggerInstall() {
  const e = deferredPrompt || lastEvent;
  if (!e) return null;
  try {
    e.prompt();
    const choice = await e.userChoice;
    deferredPrompt = null;
    notify();
    return choice?.outcome || null;
  } catch (_) {
    deferredPrompt = null;
    notify();
    return null;
  }
}

export function subscribeInstallState(cb) {
  listeners.add(cb);
  // Fire current state synchronously for new subscribers
  cb({ canInstall: !!deferredPrompt, installed, isIOS: detectIOS(), isStandalone: detectStandalone() });
  return () => listeners.delete(cb);
}

// Persistence helpers — track dismissals and visit count so we don't pester.
const VISIT_KEY = 'sg_pwa_visit_count';
const DISMISS_KEY = 'sg_pwa_dismissed_at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export function bumpVisitCount() {
  try {
    const n = Number(localStorage.getItem(VISIT_KEY) || '0') + 1;
    localStorage.setItem(VISIT_KEY, String(n));
    return n;
  } catch (_) { return 1; }
}
export function getVisitCount() {
  try { return Number(localStorage.getItem(VISIT_KEY) || '0'); }
  catch (_) { return 0; }
}
export function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
}
export function isRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || '0');
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_COOLDOWN_MS;
  } catch (_) { return false; }
}

// Service worker registration. Idempotent. Notifies via a custom event when a
// new SW has installed and is waiting to take over (the page-level "Update
// available, refresh?" toast can listen for this).
export function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return; // CRA dev server doesn't serve SW reliably
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            window.dispatchEvent(new CustomEvent('sg:sw-update-available', { detail: { reg } }));
          }
        });
      });
    }).catch(() => {});
  });
}

export function applyServiceWorkerUpdate() {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg?.waiting) reg.waiting.postMessage('SKIP_WAITING');
    setTimeout(() => window.location.reload(), 300);
  });
}
